import React from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Table, Tag, Card, Row, Col, theme, Collapse, Button } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { Field } from '../../components/Field';

const ENTITY_TYPE_COLORS: Record<string, string> = {
  DISH: '#3ecf8e',
  FOOD_LABEL: '#1890ff',
  EQUIPMENT: '#f5a623',
  SPORT_ACTIVITY: '#722ed1',
  FOOD_BARCODE: '#eb2f96',
};

export const VisualEntityShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow({ resource: 'visual-entities' });
  const item = queryResult?.data?.data as any;

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const meta = item.metadata as Record<string, any> | undefined;
  const macros = meta?.macros as Record<string, any> | undefined;
  const translations = item.translations as Record<string, any> | undefined;
  const typeColor = ENTITY_TYPE_COLORS[item.entityType] || '#898989';
  const imageUrl = (item.metadata as any)?._imageUrl;

  return (
    <Show resource="visual-entities">
      <div style={{ padding: '8px 0' }}>
        <Row gutter={24}>
          <Col span={12}>
            {imageUrl && (
              <div style={{ marginBottom: 16 }}>
                <img
                  src={imageUrl}
                  alt={item.name}
                  style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8, border: `1px solid ${token.colorBorderSecondary}` }}
                />
              </div>
            )}
            <Field label="Name">{item.name}</Field>
            <Field label="Entity Type">
              <Tag style={{ borderColor: typeColor, color: typeColor, background: `${typeColor}15`, borderRadius: 9999, padding: '2px 12px', fontWeight: 500 }}>
                {item.entityType}
              </Tag>
            </Field>
            <Field label="Description">{item.description || '—'}</Field>
            <Field label="Confidence">
              {item.confidence != null ? `${(item.confidence * 100).toFixed(1)}%` : '—'}
            </Field>
            <Field label="Source">{item.sourceProvider || '—'}</Field>
            <Field label="Model">{item.sourceModel || '—'}</Field>
            <Field label="Tags">
              {(item.tags?.length > 0) ? (
                <span>{item.tags.map((t: string) => t).join(', ')}</span>
              ) : '—'}
            </Field>
          </Col>
          <Col span={12}>
            <Field label="Owner ID">
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.ownerId || '—'}</span>
            </Field>
            <Field label="Knowledge Base">{item.knowledgeBaseName || '—'}</Field>
            <Field label="Created At">{new Date(item.createdAt).toLocaleString()}</Field>
            <Field label="Updated At">{new Date(item.updatedAt).toLocaleString()}</Field>
          </Col>
        </Row>
      </div>

      {/* Nutrition / Macros */}
      {macros && (
        <Card title="Nutrition (from metadata)" size="small" style={{ marginTop: 24 }}>
          <Row gutter={24}>
            <Col span={8}><Field label="Calories">{meta?.estimatedCalories ? `${meta.estimatedCalories} kcal` : '—'}</Field></Col>
            <Col span={8}><Field label="Protein">{macros.protein != null ? `${macros.protein}g` : '—'}</Field></Col>
            <Col span={8}><Field label="Fat">{macros.fat != null ? `${macros.fat}g` : '—'}</Field></Col>
            <Col span={8}><Field label="Carbs">{macros.carbs != null ? `${macros.carbs}g` : '—'}</Field></Col>
            <Col span={8}><Field label="Fiber">{macros.fiber != null ? `${macros.fiber}g` : '—'}</Field></Col>
          </Row>
        </Card>
      )}

      {/* Translations */}
      {translations && Object.keys(translations).length > 0 && (
        <Card title="Translations" size="small" style={{ marginTop: 16 }}>
          <Collapse ghost>
            {Object.entries(translations).map(([locale, data]: [string, any]) => (
              <Collapse.Panel header={
                <span><Tag style={{ borderRadius: 9999, padding: '2px 8px', marginRight: 8 }}>{locale}</Tag> {data.name}</span>
              } key={locale}>
                <Field label="Name">{data.name}</Field>
                {data.components?.length > 0 && (
                  <Field label="Components">
                    <Table
                      dataSource={data.components}
                      rowKey={(_: any, i: number | undefined) => `${locale}-${i ?? 0}`}
                      pagination={false}
                      size="small"
                    >
                      <Table.Column dataIndex="name" title="Name" />
                    </Table>
                  </Field>
                )}
              </Collapse.Panel>
            ))}
          </Collapse>
        </Card>
      )}

      {/* Ingredients (from DishCatalog) */}
      {item.ingredients?.length > 0 && (
        <Card title="Ingredients (Catalog)" size="small" style={{ marginTop: 16 }}>
          <Table
            dataSource={item.ingredients}
            rowKey="id"
            pagination={false}
            size="small"
          >
            <Table.Column dataIndex="displayName" title="Name" width={200} />
            <Table.Column title="Catalog Match" width={180} render={(_: any, r: any) =>
              r.ingredientCatalog
                ? <Tag color="green">{r.ingredientCatalog.name}</Tag>
                : <Tag>Unmatched</Tag>
            } />
            <Table.Column dataIndex="weightGrams" title="Weight (g)" width={100} render={(v: number) => v > 0 ? `${v}g` : '—'} />
            <Table.Column title="Category" width={120} render={(_: any, r: any) => r.ingredientCatalog?.category || '—'} />
          </Table>
        </Card>
      )}

      {/* Legacy Components (child entities — for old records) */}
      {!item.ingredients?.length && item.parentRelations?.length > 0 && (
        <Card title="Components" size="small" style={{ marginTop: 16 }}>
          <Table
            dataSource={item.parentRelations}
            rowKey="id"
            pagination={false}
            size="small"
          >
            <Table.Column dataIndex="childEntity" title="Name" width={200} render={(e: any) => e?.name || '—'} />
            <Table.Column dataIndex="relationType" title="Relation" width={120} />
            <Table.Column dataIndex="quantity" title="Quantity" width={100} render={(v: string) => v || '—'} />
            <Table.Column dataIndex="confidence" title="Confidence" width={100} render={(v: number) => v != null ? `${(v * 100).toFixed(0)}%` : '—'} />
          </Table>
        </Card>
      )}

      {/* Linked LLM Call Log */}
      {meta?._llmCallLogId && (
        <Card title="LLM Call Log" size="small" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{meta._llmCallLogId}</span>
            <a href={`/llm-call-logs/show/${meta._llmCallLogId}`}>
              <Button type="link" size="small" icon={<LinkOutlined />}>View Call Log</Button>
            </a>
          </div>
        </Card>
      )}

      {/* Full metadata JSON */}
      <Card title="Raw Metadata" size="small" style={{ marginTop: 16 }}>
        <pre style={{ fontSize: 12, maxHeight: 400, overflow: 'auto', background: token.colorBgLayout, padding: 12, borderRadius: 8 }}>
          {JSON.stringify(item.metadata, null, 2)}
        </pre>
      </Card>
    </Show>
  );
};
